<script lang="ts" setup>
import {
  LoadingSpinner,
  Modal,
  Tooltip,
  Search,
  UpgradeModal,
  CreateDocumentSpaceModal,
} from "@/components";
import { ref, computed, watch } from "vue";
import { gql } from "@urql/core";
import { useQuery } from "@urql/vue";
import { useSpaceModalDisplay } from "@/composables";
import { useStore } from "vuex";

const store = useStore();
const canUseSharedSpaces = computed(() =>
  store.getters.user.canUseSharedSpaces(),
);

type SpacePickerSpace = {
  id: string;
  name?: string | null;
  createdAt: Date;
  updatedAt: Date;
  existingDocumentItems?: {
    nodes: Array<{ documentId?: string | null } | null>;
  };
  existingFlowItems?: { nodes: Array<{ flowId?: string | null } | null> };
  config?: { name?: string | null } | null;
};

const DocumentSpacePickerDocument = gql`
  query DocumentSpacePicker(
    $search: String!
    $documentId: BigInt
    $flowId: BigInt
  ) {
    documentSpaces(
      filter: { name: { includesInsensitive: $search } }
      orderBy: CREATED_AT_DESC
    ) {
      nodes {
        id
        name
        createdAt
        updatedAt
        existingDocumentItems: documentSpaceItems(
          condition: { documentId: $documentId }
        ) {
          nodes {
            documentId
          }
        }
        existingFlowItems: documentSpaceItems(condition: { flowId: $flowId }) {
          nodes {
            flowId
          }
        }
        config {
          name
        }
      }
    }
  }
`;

const props = defineProps({
  inheritClassicTheme: { type: Boolean, default: false },
  documentId: {
    type: String,
    required: false,
  },
  flowId: {
    type: String,
    required: false,
  },
});

const {
  displayCreateDocumentSpaceModal,
  displayUpgradeModal,
  upgradeModalVisible,
  handleModalDisplay,
} = useSpaceModalDisplay();

const emit = defineEmits([
  "close",
  "submit",
  "errorCreatingSpace",
  "errorInvalidInput",
]);
const emitClose = () => emit("close");
const emitSubmit = () => {
  emit("submit", selectedSpaces.value);
};

enum State {
  ADD_TO_EXISTING,
  ADD_TO_NEW,
}

const currentState = ref<State>(State.ADD_TO_EXISTING);

const search = ref<string>("");
const setSearch = (value: string) => {
  search.value = value;
};

const { data, fetching, executeQuery } = useQuery<{
  documentSpaces?: { nodes: Array<SpacePickerSpace | null> } | null;
}>({
  query: DocumentSpacePickerDocument,
  variables: computed(() => ({
    search: search.value,
    documentId: props.documentId ?? null,
    flowId: props.flowId ?? null,
  })),
  requestPolicy: "cache-and-network",
  pause: computed(() => !canUseSharedSpaces.value),
});

const newSpaces = ref<Array<string>>([]);

const handleNewSpace = async (spaceId: string) => {
  await executeQuery();
  newSpaces.value.push(spaceId);
  currentState.value = State.ADD_TO_EXISTING;
  selectedSpaces.value.push(spaceId);
};

const changeState = async () => {
  if (!canUseSharedSpaces.value) return;
  await handleModalDisplay("create");
  if (displayCreateDocumentSpaceModal.value) {
    currentState.value =
      currentState.value === State.ADD_TO_NEW
        ? State.ADD_TO_EXISTING
        : State.ADD_TO_NEW;

    search.value = "";
  }
};

watch(
  canUseSharedSpaces,
  (enabled) => {
    if (!enabled) emitClose();
  },
  { immediate: true },
);

const selectedSpaces = ref<Array<string>>([]);
const toggleSpace = (spaceId: string) => {
  if (selectedSpaces.value.includes(spaceId)) {
    selectedSpaces.value.splice(selectedSpaces.value.indexOf(spaceId), 1);
  } else {
    selectedSpaces.value.push(spaceId);
  }
};

const hasExistingDocument = (space: SpacePickerSpace) => {
  return (
    !!props.documentId && (space.existingDocumentItems?.nodes.length ?? 0) > 0
  );
};

const hasExistingFlow = (space: SpacePickerSpace) => {
  return !!props.flowId && (space.existingFlowItems?.nodes.length ?? 0) > 0;
};

const isDisabled = (space: SpacePickerSpace) => {
  return hasExistingDocument(space) || hasExistingFlow(space);
};

const disabledTooltip = (space: SpacePickerSpace) => {
  if (hasExistingFlow(space)) {
    return "documentSpacePicker.alreadySelectedFlow";
  }

  return "documentSpacePicker.alreadySelected";
};

const toggleAllItemsSelection = () => {
  if (allItemsSelected.value) {
    selectedSpaces.value = [];
  } else {
    selectedSpaces.value = data
      .value!.documentSpaces!.nodes.filter(
        (space): space is SpacePickerSpace => space !== null,
      )
      .filter((space) => !isDisabled(space))
      .map((space) => space.id);
  }
};

const allItemsSelected = computed(() =>
  data.value?.documentSpaces?.nodes
    .filter((space): space is SpacePickerSpace => space !== null)
    .every(
      (space: SpacePickerSpace) =>
        isDisabled(space) || selectedSpaces.value.includes(space.id),
    ),
);
</script>

<template>
  <Modal
    :active="true"
    class="content_scroll medium_large"
    :class="{ 'classic-theme-light-only': !inheritClassicTheme }"
    @close="emitClose"
    v-if="currentState === State.ADD_TO_EXISTING"
  >
    <template v-slot:header>
      {{ $t("documentSpacePicker.header") }}
    </template>

    <template v-slot:body>
      <div class="body_container">
        <div class="controls">
          <Search
            :inherit-classic-theme="inheritClassicTheme"
            :placeholder="$t('documentSpacePicker.search.placeholder')"
            @change="setSearch"
            @submit="setSearch"
          />

          <button class="button primary right" @click="changeState">
            <span class="material-symbols-outlined"> add </span>
            {{ $t("documentSpacePicker.createNew.buttonText") }}
          </button>
        </div>
        <div v-if="fetching" class="loading_status">
          <LoadingSpinner
            :color="
              inheritClassicTheme ? 'var(--classic-text, black)' : 'black'
            "
          />
        </div>
        <div
          v-else-if="
            data?.documentSpaces?.nodes &&
            data?.documentSpaces?.nodes.length > 0
          "
          class="results"
        >
          <div class="container">
            <input
              type="checkbox"
              @change="toggleAllItemsSelection"
              :checked="allItemsSelected"
            />
          </div>

          <ul>
            <li
              v-for="space in data.documentSpaces.nodes"
              :key="space!.id!"
              :class="{ disabled: isDisabled(space as SpacePickerSpace) }"
              @click.stop="
                !isDisabled(space as SpacePickerSpace) && toggleSpace(space!.id)
              "
            >
              <Tooltip class="left">
                <template v-slot>
                  <div class="flex_container">
                    <input
                      type="checkbox"
                      :value="space!.id"
                      :checked="
                        selectedSpaces.includes(space!.id) ||
                        isDisabled(space as SpacePickerSpace)
                      "
                      :disabled="isDisabled(space as SpacePickerSpace)"
                    />
                    {{ space!.name! }}
                    <span
                      v-if="store.getters.numSpaceConfigs > 1"
                      class="badge"
                    >
                      {{ space!.config?.name }}
                    </span>
                  </div>
                </template>
                <template v-if="isDisabled(space as SpacePickerSpace)" #tooltip>
                  {{ $t(disabledTooltip(space as SpacePickerSpace)) }}
                </template>
              </Tooltip>

              <label v-if="newSpaces.includes(space!.id)" class="label">
                {{ $t("new") }}
              </label>
            </li>
          </ul>
        </div>
        <div v-else class="info">
          <div>
            {{ $t("documentSpacePicker.emptyList") }}
          </div>
        </div>
      </div>
    </template>

    <template v-slot:footer>
      <label v-if="selectedSpaces.length > 0">
        {{
          $t("documentSpacePicker.submitLabel", {
            count: selectedSpaces.length,
          })
        }}</label
      >
      <div class="buttons">
        <button class="button left" @click="emitClose">
          {{ $t("cancel") }}
        </button>
        <button
          class="button primary"
          :class="{ disabled: selectedSpaces.length === 0 }"
          @click="emitSubmit"
        >
          {{ $t("submit") }}
        </button>
      </div>
    </template>
  </Modal>

  <CreateDocumentSpaceModal
    v-else-if="canUseSharedSpaces && currentState === State.ADD_TO_NEW"
    :redirect="false"
    :inherit-classic-theme="inheritClassicTheme"
    @success="handleNewSpace"
    @error="emit('errorCreatingSpace')"
  >
    <template #header>
      <span
        class="material-symbols-outlined"
        @click="currentState = State.ADD_TO_EXISTING"
      >
        arrow_back
      </span>
    </template>
  </CreateDocumentSpaceModal>

  <upgrade-modal
    v-if="upgradeModalVisible"
    :inherit-classic-theme="inheritClassicTheme"
    @close="displayUpgradeModal = false"
  />
</template>

<style scoped lang="scss">
.results {
  height: calc(100% - 60px);
  overflow: scroll;
  font-size: 0.9rem;
}

ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
  width: 100%;
}

ul li {
  line-height: 2.5;
  border-top: 1px solid var(--classic-border-subtle, #dbdbdb);
  display: flex;
  align-items: center;
  padding: 0.3em 0;
}

ul li:last-child {
  border-bottom: none;
}

ul li:hover {
  cursor: pointer;
  background: var(--classic-surface-hover, #f9fafb);
}

ul li.disabled:hover {
  cursor: initial;
}

input[type="checkbox"] {
  font-size: 0.9em;
  margin-right: 0.5em;
}

.controls {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 0.3em;
}

.controls .button {
  margin-left: auto;
}

label {
  font-size: 0.9rem;
  line-height: 2;
}
.body_container {
  height: 100%;
}

.loading_status {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 75%;
}

.label {
  text-transform: uppercase;
  color: var(--classic-accent, $color-secondary);
  margin-left: auto;
  padding-right: 0.3em;
}

.info {
  font-size: 0.9rem;
  height: calc(100% - 42px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-style: italic;
}

.container {
  padding: 0.3em 0 0.5em 0;
}

.badge {
  align-items: center;
  border-radius: 4px;
  padding: 2px 6px;
  background: var(--classic-accent-soft, #d6fafb);
  font-size: 0.7em;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 0.5em;
}

.modal .footer .buttons {
  width: auto;
}
</style>
